import { Franchise } from '@/types';
import {
  Button,
  Flex,
  Form,
  FormInstance,
  Input,
  message,
  Radio,
  Select,
  Typography,
  Upload,
  UploadFile,
  UploadProps,
  Image,
  DatePicker,
} from 'antd';
import './index.scss';
import '../free-listing-field.scss';
import { SvgIcon } from '@/components/icon';
import industries from '@/industries.json';
import { DropdownHeaderWithAction } from '@/components/shared';
import React, { useEffect, useState } from 'react';
import { DefaultOptionType } from 'antd/es/select';
import { statesProvinces, filterStateOption } from '@/citiesWithStates';
import { useWatch } from 'antd/es/form/Form';
import { getBase64 } from '@/helpers/getBase64';
import { AvatarPlaceholder } from '@/components/shared/avatar-placeholder';
import ImageCropper from '@/components/image-croper';
import { blobToFile } from '@/helpers/blobToFile';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateFranchise } from '@/api/admin';
import dayjs from 'dayjs';
import { RuleObject } from 'antd/es/form';
import { Profile } from '@/pages/my-profile/general';
import { phoneRules } from '@/helpers/validation';

interface FranchiseEditProps {
  form: FormInstance<Partial<Franchise>>;
  franchise: Partial<Franchise>;
  onCancel: () => void;
}
export const FranchiseEdit = ({
  franchise,
  form,
  onCancel,
}: FranchiseEditProps): React.JSX.Element => {
  const [isAllStatesSelected, setIsAllStatesSelected] = useState(false);
  const [avatar, setAvatar] = useState<string>('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [dirtyFields, setDirtyFields] = useState<Partial<Profile>>({});

  const [imageToCrop, setImageToCrop] = useState<string>('');
  const queryClient = useQueryClient();

  const selectedStates = useWatch('statesRegisteredIn', form) || [];

  const [stateProvinceOptions, setStateProvinceOptions] = useState<
    DefaultOptionType[]
  >([]);

  const industriesOptions = industries.data.map((industry) => ({
    value: industry,
    label: industry,
  }));

  const countryOptions: DefaultOptionType[] = [
    { value: 'USA', label: 'USA' },
    { value: 'Canada', label: 'Canada' },
  ];

  const validationRules = {
    required: (fieldName: string) => ({
      required: true,
      validator: (_: unknown, value: string) => {
        if (value && value.trim() !== '') return Promise.resolve();
        return Promise.reject(new Error(`${fieldName} is required`));
      },
    }),
    establishedYear: [
      ({ getFieldValue }: { getFieldValue: (field: string) => string }) => ({
        validator(_: RuleObject, value: string) {
          const startedFranchising = getFieldValue('startedFranchising');
          // If startedFranchising is set but established is being removed/cleared
          if (startedFranchising && !value) {
            return Promise.reject(
              new Error(
                'Established year is required when Started Franchising year is set',
              ),
            );
          }
          getFieldValue('startedFranchising');
          // If startedFranchising is set but established is being removed/cleared
          if (startedFranchising && !value) {
            return Promise.reject(
              new Error(
                'Established year is required when Started Franchising year is set',
              ),
            );
          }

          return Promise.resolve();
        },
      }),
    ],
    startedFranchisingYear: [
      ({ getFieldValue }: { getFieldValue: (field: string) => string }) => ({
        validator(_: RuleObject, value: string) {
          const established = getFieldValue('established');

          if (!value || !established) {
            return Promise.resolve();
          }
          const startedYear = dayjs(value);
          const establishedYear = dayjs(established);

          if (
            startedYear.isSame(establishedYear, 'year') ||
            startedYear.isAfter(establishedYear, 'year')
          ) {
            return Promise.resolve();
          }

          return Promise.reject(
            new Error(
              'Started Franchising year must be the same or later than Established year',
            ),
          );
        },
      }),
    ],
    number: {
      pattern: /^\d+$/,
      message: 'Please enter a valid number',
    },
  };

  const handleFinishFailed = (errorInfo: any) => {
    message.error('Please fill in all required fields correctly');
    console.log('Validation failed:', errorInfo);
  };

  const handleChangePhoto: UploadProps['onChange'] = async (info) => {
    const { fileList: newFileList } = info;

    try {
      if (info.file.status === 'done') {
        setImageToCrop(await getBase64(info.file.originFileObj));
      }

      if (info.file.status === 'removed') {
        setAvatar('');
      }

      setFileList(newFileList);
    } catch (error) {
      message.error('Failed to update profile. Please try again.');
    } finally {
      /* empty */
    }
  };

  const uploadProps: UploadProps = {
    name: 'logo',
    fileList,
    multiple: false,
    maxCount: 1,
    accept: 'image/jpeg, image/png',
    onChange: handleChangePhoto,
    customRequest({ onSuccess }) {
      if (onSuccess) {
        onSuccess('ok');
      }
    },
    onRemove: () => {
      form.setFieldsValue({ logo: '' });
      setAvatar(franchise?.logo || '');
    },
  };

  useEffect(() => {
    const isAllSelected =
      selectedStates?.length === stateProvinceOptions.length;

    if (isAllSelected) {
      setIsAllStatesSelected(true);
    } else {
      setIsAllStatesSelected(false);
    }
  }, [form, selectedStates?.length, stateProvinceOptions.length]);

  useEffect(() => {
    setAvatar(franchise?.logo || '');
  }, [franchise?.logo]);

  useEffect(() => {
    const filteredStatesProvinces = statesProvinces.filter(
      (item) => item.country === franchise?.country || 'US',
    );

    setStateProvinceOptions(
      filteredStatesProvinces.map((item) => ({
        label: item.name,
        value: item.code,
      })),
    );

    form.setFieldsValue({
      stateProvince: undefined,
      city: undefined,
    });
  }, [franchise, form]);

  const selectAllStates = () => {
    const allStates = stateProvinceOptions.map(
      (state) => state.value as string,
    );

    if (!isAllStatesSelected) {
      form.setFieldValue('statesRegisteredIn', allStates);
    } else {
      form.setFieldValue('statesRegisteredIn', []);
    }
  };

  const { mutateAsync: updateFranchiseMutation } = useMutation({
    mutationFn: updateFranchise,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['franchisor', franchise.id],
      });
    },
  });

  const handleFinish = async (values: Partial<Franchise>) => {
    const data: Partial<Franchise> = {};

    Object.keys(values).forEach((key) => {
      const typedKey = key as keyof Franchise;
      const value = values[typedKey];

      if (dirtyFields?.[typedKey]) {
        (data as any)[typedKey] = value;
      }
    });

    if (Object.keys(data).length === 0 && !avatar) {
      message.error('No changes detected');
      return;
    }

    const avatarFile = avatar.startsWith('blob:')
      ? await blobToFile(avatar, 'cropped-image.jpg')
      : undefined;

    try {
      await updateFranchiseMutation(
        {
          data,
          avatar: avatarFile,
          id: franchise?.id || '',
        },
        {
          onSuccess: () => {
            setFileList([]);
            setDirtyFields({});
          },
        },
      );
      message.success('Franchise updated successfully');
    } catch (error: any) {
      console.error('Edit Franchise Error', error);
      message.error(
        error?.response?.data?.message ||
          'Failed to update franchise. Please try again.',
      );
    }
  };

  return (
    <Flex gap={24} flex={1}>
      <div className={'edit-wrapper'} style={{ flex: 1 }}>
        <Typography.Title level={4} style={{ margin: '0 0 24px' }}>
          Franchise Details
        </Typography.Title>
        <Form
          form={form}
          layout={'vertical'}
          name={'basic'}
          onFinish={handleFinish}
          onFinishFailed={handleFinishFailed}
          onValuesChange={(changedValues) => {
            Object.keys(changedValues).forEach((key) => {
              setDirtyFields((prev) => {
                return {
                  ...prev,
                  [key]: true,
                };
              });
            });
          }}
          className={'admin-forgot-form'}
        >
          <Flex justify={'space-between'} align={'center'} gap={24}>
            <Form.Item
              style={{ flex: 1 }}
              name={'company'}
              label={<label style={{ color: '#9FA1AD' }}>Brand Name</label>}
              rules={[validationRules.required('Brand Name')]}
              required={false}
              validateTrigger={['onChange', 'onBlur']}
            >
              <Input placeholder={'Enter here'} className={'edit-input'} />
            </Form.Item>
            <Form.Item
              name={'country'}
              style={{ flex: 1 }}
              label={<label style={{ color: '#9FA1AD' }}>Country</label>}
            >
              <Select
                showSearch
                options={countryOptions}
                size={'large'}
                className={'edit-select'}
              />
            </Form.Item>
          </Flex>
          <Flex justify={'space-between'} align={'center'} gap={24}>
            <Form.Item
              style={{ flex: 1 }}
              name={'established'}
              label={<label style={{ color: '#9FA1AD' }}>Established</label>}
              rules={validationRules.establishedYear}
              required={false}
              validateTrigger={['onChange', 'onBlur']}
            >
              {/* <Input placeholder="Enter here" className="edit-input" /> */}

              <DatePicker
                style={{ width: '100%' }}
                placeholder={''}
                // value={form.getFieldValue('established')}
                disabledDate={(current) => current.isAfter(dayjs(), 'year')}
                picker={'year'}
              />
            </Form.Item>
            <Form.Item
              style={{ flex: 1 }}
              name={'startedFranchising'}
              label={
                <label style={{ color: '#9FA1AD' }}>Started Franchising</label>
              }
              rules={validationRules.startedFranchisingYear}
              required={false}
              validateTrigger={['onChange', 'onBlur']}
              dependencies={['established']}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder={''}
                // value={form.getFieldValue('startedFranchising')}
                picker={'year'}
                disabledDate={(current) => {
                  const established = form.getFieldValue('established');
                  if (!current) return false;
                  const establishedDate = established
                    ? dayjs(established)
                    : false;
                  return (
                    current.isAfter(dayjs(), 'year') ||
                    (establishedDate &&
                      current.isBefore(establishedDate, 'year'))
                  );
                }}
              />
            </Form.Item>
          </Flex>
          <Flex justify={'space-between'} align={'center'} gap={24}>
            <Form.Item
              name={'franchisedUnits'}
              style={{ flex: 1 }}
              label={
                <label style={{ color: '#9FA1AD' }}>Franchised Units</label>
              }
              rules={[validationRules.number]}
            >
              <Input placeholder={'Enter here'} className={'edit-input'} />
            </Form.Item>
            <Form.Item
              name={'corporateUnits'}
              style={{ flex: 1 }}
              label={
                <label style={{ color: '#9FA1AD' }}>Corporate Units</label>
              }
              rules={[validationRules.number]}
            >
              <Input placeholder={'Enter here'} className={'edit-input'} />
            </Form.Item>
          </Flex>
          <Flex justify={'space-between'} align={'center'} gap={24}>
            <Flex gap={12} style={{ flex: 1 }}>
              <Flex vertical gap={12} style={{ flex: 1 }}>
                <label style={{ color: '#9FA1AD' }}>Franchises In Canada</label>
                <Flex wrap gap={8}>
                  <Form.Item name={'franchisesInCanada'}>
                    <Radio.Group>
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Flex>
              </Flex>
              <Flex vertical gap={12}>
                <label style={{ color: '#9FA1AD' }}>
                  International Franchises
                </label>
                <Flex wrap gap={8}>
                  <Form.Item name={'otherInternationalFranchises'}>
                    <Radio.Group>
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Flex>
              </Flex>
              <Flex vertical gap={12}>
                <label style={{ color: '#9FA1AD' }}>SBA Eligible</label>
                <Flex wrap gap={8}>
                  <Form.Item name={'sbaEligible'}>
                    <Radio.Group>
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Flex>
              </Flex>
              <Flex vertical gap={12}>
                <label style={{ color: '#9FA1AD' }}>E2 Visa Friendly</label>
                <Flex wrap gap={8}>
                  <Form.Item name={'e2VisaFriendly'}>
                    <Radio.Group>
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Flex>
              </Flex>
            </Flex>
            <Form.Item
              name={'categories'}
              style={{ flex: 1 }}
              label={<label style={{ color: '#9FA1AD' }}>Categories</label>}
            >
              <Select
                showSearch
                mode={'tags'}
                maxTagCount={2}
                // options={item.options}
                className={'edit-select'}
                suffixIcon={
                  <SvgIcon style={{ zIndex: 100 }} type={'chevronDown'} />
                }
                options={industriesOptions}
                size={'large'}
                placeholder={'Type to search or create new'}
              />
            </Form.Item>
          </Flex>
          <Flex justify={'space-between'} align={'center'} gap={24}>
            <div className={'free-listing-field'}>
              <div className={'free-listing-title'}>
                Free Listing <span className={'free-listing-required'}>*</span>
              </div>
              <div className={'free-listing-question'}>
                Is this franchisor a Free Listing?{' '}
                <span className={'free-listing-required'}>Required</span>
              </div>
              {/* Boolean radio: reuse of validationRules.required is unsafe
                  (it calls value.trim() and treats false as empty), so
                  validate presence explicitly. */}
              <Form.Item
                name={'isFreeListing'}
                className={'free-listing-control'}
                rules={[
                  {
                    validator: (_: any, value: any) =>
                      typeof value === 'boolean'
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              'Please indicate whether this franchisor is a Free Listing.',
                            ),
                          ),
                  },
                ]}
              >
                <Radio.Group>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
            <div className={'free-listing-spacer'} />
          </Flex>
          <Flex justify={'center'} align={'center'} gap={24}>
            <Form.Item
              name={'overview'}
              style={{ flex: 1 }}
              label={<label style={{ color: '#9FA1AD' }}>Overview</label>}
            >
              <Input.TextArea
                placeholder={'Enter here'}
                className={'edit-input'}
                style={{ height: '100px' }}
              />
            </Form.Item>
          </Flex>
          <Flex justify={'center'} align={'center'} gap={24}>
            <Form.Item
              name={'statesRegisteredIn'}
              style={{ flex: 1 }}
              label={
                <label style={{ color: '#9FA1AD' }}>States Registered In</label>
              }
            >
              <Select
                showSearch
                // TCX-476: match the state name or its code
                filterOption={filterStateOption}
                mode={'multiple'}
                maxTagCount={5}
                // options={item.options}
                dropdownRender={(menu) => (
                  <Flex vertical>
                    {
                      <DropdownHeaderWithAction
                        headerTitle={'Choose States'}
                        buttonText={
                          isAllStatesSelected ? 'Deselect All' : 'Select All'
                        }
                        action={selectAllStates}
                      />
                    }
                    <div
                      style={{
                        padding: 0,
                        borderRadius: 24,
                      }}
                    >
                      {menu}
                    </div>
                  </Flex>
                )}
                className={'edit-select'}
                suffixIcon={
                  <SvgIcon style={{ zIndex: 100 }} type={'chevronDown'} />
                }
                options={stateProvinceOptions}
                size={'large'}
                placeholder={'Type to search'}
              />
            </Form.Item>
          </Flex>

          <div
            style={{
              height: 1,
              width: '100%',
              backgroundColor: '#D9D9D9',
              margin: '24px 0',
            }}
          />

          <Typography.Title level={4} style={{ margin: '0 0 24px' }}>
            Company Info
          </Typography.Title>

          <Flex justify={'space-between'} align={'center'} gap={24}>
            <Form.Item
              style={{ flex: 1 }}
              name={'workPhone'}
              rules={phoneRules}
              label={<label style={{ color: '#9FA1AD' }}>Phone</label>}
            >
              <Input placeholder={'Enter here'} className={'edit-input'} />
            </Form.Item>
            <Form.Item
              style={{ flex: 1 }}
              name={'corporateWebsite'}
              label={
                <label style={{ color: '#9FA1AD' }}>Corporate Website</label>
              }
            >
              <Input placeholder={'Enter here'} className={'edit-input'} />
            </Form.Item>
          </Flex>

          <Flex justify={'space-between'} align={'center'} gap={24}>
            <Form.Item
              name={'corporateAddress'}
              style={{ flex: 1 }}
              label={<label style={{ color: '#9FA1AD' }}>Address</label>}
            >
              <Input placeholder={'Enter here'} className={'edit-input'} />
            </Form.Item>
            <Form.Item
              style={{ flex: 1 }}
              name={'premiumDashboardUrl'}
              rules={[
                {
                  validator: (_: RuleObject, value: string) => {
                    if (!value) return Promise.resolve();
                    try {
                      const { protocol, hostname } = new URL(value);
                      // Mirror the server rule (@IsUrl: http(s) + a real host
                      // with a TLD) so a value accepted here isn't rejected by
                      // the backend with a 400.
                      const isHttp =
                        protocol === 'http:' || protocol === 'https:';
                      if (isHttp && hostname.includes('.')) {
                        return Promise.resolve();
                      }
                    } catch {
                      // fall through to rejection below
                    }
                    return Promise.reject(
                      new Error(
                        'Enter a valid URL starting with http:// or https://',
                      ),
                    );
                  },
                },
              ]}
              label={
                <label style={{ color: '#9FA1AD' }}>
                  TCX Premium Dashboard URL
                </label>
              }
            >
              <Input placeholder={'Enter here'} className={'edit-input'} />
            </Form.Item>
          </Flex>

          <Form.Item style={{ marginBottom: 0 }}>
            <Flex align={'center'} gap={8}>
              <Button
                type={'primary'}
                className={'edit-button'}
                onClick={() => form.submit()}
                // loading={isPending}
                // disabled={isPending}
              >
                Save Changes
              </Button>
              <Button
                type={'default'}
                className={'edit-button'}
                onClick={onCancel}
                // loading={isPending}
                // disabled={isPending}
              >
                Cancel
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </div>
      <div className={'edit-wrapper'} style={{ height: '30%' }}>
        <label style={{ color: '#9FA1AD', paddingBottom: '24px' }}>Icon</label>
        {imageToCrop.length > 0 && (
          <ImageCropper
            imageToCrop={imageToCrop}
            setImageToCrop={setImageToCrop}
            setAvatar={setAvatar}
            fitWholeImage
          />
        )}
        <Flex align={'center'} vertical style={{ margin: '0 auto' }}>
          <div style={{ position: 'relative' }}>
            {avatar ? (
              <React.Fragment>
                <Image
                  src={avatar}
                  wrapperClassName={'avatar'}
                  wrapperStyle={{
                    borderRadius: 16,
                    overflow: 'hidden',
                    background: '#fff',
                  }}
                  height={180}
                  width={180}
                  style={{
                    borderRadius: 16,
                    objectFit: 'contain',
                    background: '#fff',
                    padding: 8,
                  }}
                />
              </React.Fragment>
            ) : (
              <div
                style={{
                  width: 180,
                  height: 180,
                  overflow: 'hidden',
                  borderRadius: '50%',
                  fontSize: 36,
                }}
              >
                <AvatarPlaceholder
                  firstName={franchise?.company || ''}
                  lastName={''}
                />
              </div>
            )}
          </div>
          <Form.Item name={'profilePhoto'} label={'Profile Photo'} noStyle>
            <Upload {...uploadProps}>
              <Button type={'default'} className={'edit-upload-image'}>
                Change Logo
              </Button>
            </Upload>
          </Form.Item>
        </Flex>
      </div>
    </Flex>
  );
};
